import { getSettings } from './getSettings';
import { checkIfMemoryIsAboveThreshold, getAllIframeMemoryInfo } from './iframeMemoryService';
import { trace } from 'owa-trace';
import { type IframeScenario } from '../settings/iframes-mapping';
import type { IframeState } from '../hooks/useIframeState';

let interval: NodeJS.Timeout | null = null;
const listeners = new Map<
    string,
    {
        scenario: IframeScenario;
        state: IframeState;
        callback: (memory: number | undefined, isAboveThreshold: boolean) => void;
    }
>();

export function registerIframeMemoryListener(
    id: string,
    scenario: IframeScenario,
    state: IframeState,
    callback: (memory: number | undefined, isAboveThreshold: boolean) => void
): void {
    trace.info(` Iframe memory listener registered: ${id} (${scenario})`, 'iframeWrapper');

    // Set the callback listener
    listeners.set(id, { scenario, state, callback });

    // Set the interval to check the memory health
    if (!interval) {
        interval = setInterval(() => checkMemoryHealth(), getSettings().backgroundCheckInterval);

        // Run the check for the first time
        checkMemoryHealth();
    }
}

export function unregisterIframeMemoryListener(id: string): void {
    trace.info(` Iframe memory listener unregistered: ${id}`, 'iframeWrapper');

    listeners.delete(id);

    if (listeners.size === 0 && interval) {
        clearInterval(interval);
        interval = null;
    }
}

async function checkMemoryHealth() {
    // Fetches fresh data from native host and updates cache automatically
    const memoryInfos = await getAllIframeMemoryInfo();

    if (!memoryInfos) {
        return;
    }

    const memorySummary = Array.from(memoryInfos.values()).map(
        m =>
            `[${m.scenario} | ${m.totalMemory}MB | #proc:${m.processes.size} (${Array.from(
                m.processes.keys()
            ).join(', ')})]`
    );
    trace.info(` Iframe memory health checked: ${memorySummary.join(', ')}`, 'iframeWrapper');

    for (const [, { scenario, state, callback }] of listeners) {
        const memoryInfo = memoryInfos.get(scenario);
        const isAboveThreshold = checkIfMemoryIsAboveThreshold(memoryInfo?.totalMemory, state);

        callback(memoryInfo?.totalMemory, isAboveThreshold);
    }
}
